import { api } from './client';
import type {
  CancelOrderDto,
  CreateFulfillmentDto,
  CreateManualOrderDto,
  CreateRefundDto,
  FileLimitsDto,
  FulfillmentAllocation,
  FulfillmentDto,
  ManualPaymentDto,
  OrderDetailDto,
  OrderListItemDto,
  OrderQueryDto,
  PagedRequest,
  PagedResult,
  ProcessReturnDto,
  RefundDto,
  ReturnRequestDto,
  ReturnStatus,
  ShipmentTrackingStatus,
  UpdateOrderNoteDto,
  UpdateTrackingDto,
  FileUploadResult,
} from './types';

/* ------------------------------------------------------------------ *
 *  Siparis, sevkiyat, iade, dosya ve kargo entegrasyonu — 25 uc
 *  siparisler (13), sevkiyat (3), iadeler (3), dosyalar (5),
 *  kargo webhook (1)
 * ------------------------------------------------------------------ */

export const adminOrdersApi = {
  /** 1. Siparis listesi; durum, tarih ve tutar filtreleriyle. */
  search: (query: OrderQueryDto = {}) =>
    api.get<PagedResult<OrderListItemDto>>('/api/admin/orders', query),

  /** 2. Yonetim gorunumu: maliyet, personel notu ve risk bilgisi dahil. */
  getById: (id: number) => api.get<OrderDetailDto>(`/api/admin/orders/${id}`),

  /** 3. Panelden manuel siparis (telefon siparisi vb.). */
  createManual: (dto: CreateManualOrderDto) =>
    api.post<OrderDetailDto>('/api/admin/orders', dto),

  /** 4. Siparisi iptal eder; stok geri alinabilir. */
  cancel: (id: number, dto: CancelOrderDto) =>
    api.postCommand(`/api/admin/orders/${id}/cancel`, dto),

  /** 5. Elle tahsilat kaydeder (havale, kapida odeme). */
  recordPayment: (id: number, dto: ManualPaymentDto) =>
    api.postCommand(`/api/admin/orders/${id}/payments`, dto),

  /** 6. Siparisi arsivler; aktif listeden cikar. */
  archive: (id: number) => api.postCommand(`/api/admin/orders/${id}/archive`),

  /** 7. Musteri ve personel notlarini gunceller. */
  updateNotes: (id: number, dto: UpdateOrderNoteDto) =>
    api.putCommand(`/api/admin/orders/${id}/notes`, dto),

  /** 8. Sevk edilmeyi bekleyen siparisler. */
  pendingFulfillment: (query: PagedRequest = {}) =>
    api.get<PagedResult<OrderListItemDto>>('/api/admin/orders/pending-fulfillment', query),

  /** 9. Siparisin hangi depolardan karsilanacagina dair oneri. */
  fulfillmentPlan: (id: number) =>
    api.get<FulfillmentAllocation[]>(`/api/admin/orders/${id}/fulfillment-plan`),

  /** 10. Sevkiyat olusturur. Odeme alinmamissa 409 doner. */
  createFulfillment: (id: number, dto: CreateFulfillmentDto) =>
    api.post<FulfillmentDto>(`/api/admin/orders/${id}/fulfillments`, dto),

  /** 11. Siparise ait iade kayitlari. */
  refunds: (id: number) => api.get<RefundDto[]>(`/api/admin/orders/${id}/refunds`),

  /**
   * 12. Iade olusturur.
   *
   * Odeme saglayicisi uzerinden alinmissa otomatik iade denenir. Havale veya
   * kapida odemede sahte basari uretilmez: iade "Pending" kalir ve personele
   * gorev duser.
   */
  createRefund: (id: number, dto: CreateRefundDto) =>
    api.post<RefundDto>(`/api/admin/orders/${id}/refunds`, dto),

  /** 13. Elle yapilan iadeyi tamamlandi isaretler; toplamlar o an guncellenir. */
  completeRefund: (refundId: number, note?: string) =>
    api.post<RefundDto>(`/api/admin/orders/refunds/${refundId}/complete`, {
      note: note ?? null,
    }),
};

export const adminFulfillmentsApi = {
  /** 14. Takip numarasi ve tasiyici bilgisini gunceller. */
  updateTracking: (id: number, dto: UpdateTrackingDto) =>
    api.put<FulfillmentDto>(`/api/admin/fulfillments/${id}/tracking`, dto),

  /** 15. Teslim edildi olarak isaretler. */
  markDelivered: (id: number, deliveredTo?: string) =>
    api.postCommand(
      `/api/admin/fulfillments/${id}/delivered` +
        (deliveredTo ? `?deliveredTo=${encodeURIComponent(deliveredTo)}` : ''),
    ),

  /** 16. Sevkiyati iptal eder; stok geri alinir. */
  cancel: (id: number, reason?: string) =>
    api.postCommand(
      `/api/admin/fulfillments/${id}/cancel` +
        (reason ? `?reason=${encodeURIComponent(reason)}` : ''),
    ),
};

export const adminReturnsApi = {
  /** 17. Iade talepleri; duruma gore filtrelenir. */
  search: (query: PagedRequest = {}, status?: ReturnStatus) =>
    api.get<PagedResult<ReturnRequestDto>>('/api/admin/returns', { ...query, status }),

  /** 18. Iade talebi detayi. */
  getById: (id: number) => api.get<ReturnRequestDto>(`/api/admin/returns/${id}`),

  /** 19. Talebi onaylar, reddeder veya inceleme sonucunu isler. */
  process: (id: number, dto: ProcessReturnDto) =>
    api.post<ReturnRequestDto>(`/api/admin/returns/${id}/process`, dto),
};

export const adminFilesApi = {
  /** 20. Tek gorsel yukler. */
  uploadImage: (file: File, folder = 'products') => {
    const form = new FormData();
    form.append('file', file);
    return api.post<FileUploadResult>(
      `/api/admin/files/images?folder=${encodeURIComponent(folder)}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  /** 20b. Tek video yukler (maks 50 MB). */
  uploadVideo: (file: File, folder = 'products') => {
    const form = new FormData();
    form.append('file', file);
    return api.post<FileUploadResult>(
      `/api/admin/files/videos?folder=${encodeURIComponent(folder)}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  /** 21. Birden fazla gorseli veya videoyu tek istekte yukler. */
  uploadImages: (files: File[], folder = 'products') => {
    const form = new FormData();
    files.forEach((file) => form.append('files', file));
    return api.post<FileUploadResult[]>(
      `/api/admin/files/images/batch?folder=${encodeURIComponent(folder)}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  /** 22. Belge yukler (fatura, katalog vb.). */
  uploadDocument: (file: File, folder = 'documents') => {
    const form = new FormData();
    form.append('file', file);
    return api.post<FileUploadResult>(
      `/api/admin/files/documents?folder=${encodeURIComponent(folder)}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  /** 23. Yuklenmis dosyayi siler. */
  remove: (storagePath: string) => api.deleteCommand('/api/admin/files', { storagePath }),

  /** 24. Yukleme kisitlari: boyut sinirlari ve izin verilen uzantilar. */
  limits: () => api.get<FileLimitsDto>('/api/admin/files/limits'),
};

/* ------------------------------------------------------------------ *
 *  Kargo firmasi bildirimi
 *
 *  Bu uc normalde tasiyicinin sunucusu tarafindan cagrilir. Panelde,
 *  bildirim gelmediginde personelin durumu elle islemesi icin kullanilir.
 * ------------------------------------------------------------------ */

export interface TrackingEventInput {
  trackingNumber: string;
  status: ShipmentTrackingStatus;
  description?: string | null;
  location?: string | null;
  occurredAt?: string | null;
}

export const shippingWebhookApi = {
  /** 25. Kargo takip olayini isler. */
  pushTrackingEvent: (dto: TrackingEventInput, carrierSecret: string) =>
    api.postCommand('/api/shipping-webhooks/tracking', dto, {
      headers: { 'X-Carrier-Secret': carrierSecret },
    }),
};
